/**
 * HOF-219 — which canvas edge carries a DOCUMENT-level unlink, and what the
 * backend call for it looks like.
 *
 * Deliberately a separate predicate from ``isManualLineEdge``, which stays
 * exactly as it is (AC 14). That one asks "are both endpoints line-spot
 * handles" and is the single gate for the line↔line unlink, including its
 * Backspace/Delete scoping; widening it would have made every document edge
 * answer a question about line pairs.
 *
 * A document edge is identified by its DATA rather than by its handles. The
 * GS→LS reference arrow anchors on ``referenz-source`` → ``title-target``, and
 * so do the Abrechnung arrow, the missing-Abrechnung placeholder arrow and the
 * Zahlungsavis arrow — the handles say where an arrow is drawn, not what it
 * asserts. Only the emitter knows it is drawing a Gutschrift↔Lieferschein
 * document edge, so the emitter stamps the two entity ids and nothing else can
 * be mistaken for one.
 */

/** What ``buildDocumentRefEdge`` stamps on the GS→LS reference arrow. */
export interface DocumentRefEdgeData {
  settlementEventId: string;
  deliveryEventId: string;
  /** ``null`` when no fact edge names this pair — see ``resolveDocumentLinkKind``. */
  linkKind: string | null;
  groupId: string;
  /** Human-readable Gutschrift label for the confirmation copy. */
  settlementLabel: string;
  /** Human-readable Lieferschein label for the confirmation copy. */
  deliveryLabel: string;
  [key: string]: unknown;
}

/** Narrow edge shape — the fields this module reads, and no more. */
export interface DocumentEdgeLike {
  id?: string;
  data?: unknown;
}

/** The backend's ``/twin/match-groups/document-link`` pair, plus its group. */
export interface DocumentUnlinkTarget {
  settlement_event_id: string;
  delivery_event_id: string;
  groupId: string;
  linkKind: string | null;
  settlementLabel: string;
  deliveryLabel: string;
}

function readEdgeData(edge: DocumentEdgeLike): Partial<DocumentRefEdgeData> | null {
  const data = edge.data;
  if (!data || typeof data !== 'object') return null;
  return data as Partial<DocumentRefEdgeData>;
}

/**
 * Does this edge carry a document-level unlink?
 *
 * Both entity ids must be present and non-blank. A partially-stamped edge is
 * refused rather than sent with an empty id: the endpoint would answer
 * ``404 event_not_found``, which reads to the operator as "your Gutschrift does
 * not exist" — a far more alarming sentence than the truth.
 */
export function isDocumentUnlinkEdge(edge: DocumentEdgeLike): boolean {
  const data = readEdgeData(edge);
  if (!data) return false;
  return (
    typeof data.settlementEventId === 'string' &&
    data.settlementEventId.trim().length > 0 &&
    typeof data.deliveryEventId === 'string' &&
    data.deliveryEventId.trim().length > 0
  );
}

/**
 * Normalise a selected edge into the pair the document-link endpoint takes, or
 * ``null`` if this edge carries no document unlink.
 *
 * Mirror of ``normaliseEdgeForUnlink`` one layer up, and the same contract: ONE
 * function decides, so the on-edge control, the context menu and any future
 * trigger cannot disagree about which edges qualify.
 */
export function normaliseDocumentEdgeForUnlink(edge: DocumentEdgeLike): DocumentUnlinkTarget | null {
  if (!isDocumentUnlinkEdge(edge)) return null;
  const data = readEdgeData(edge) as DocumentRefEdgeData;
  return {
    settlement_event_id: data.settlementEventId,
    delivery_event_id: data.deliveryEventId,
    groupId: typeof data.groupId === 'string' ? data.groupId : '',
    linkKind: typeof data.linkKind === 'string' ? data.linkKind : null,
    settlementLabel: typeof data.settlementLabel === 'string' ? data.settlementLabel : '',
    deliveryLabel: typeof data.deliveryLabel === 'string' ? data.deliveryLabel : '',
  };
}

/**
 * The ``link_kind`` the materialiser recorded for this pair, or ``null``.
 *
 * ``null`` is a real and common answer, not a lookup failure: the canvas draws
 * its GS→LS arrow from ``delivery_reference`` and falls back to the first
 * Lieferschein when nothing resolves, so an arrow can be on screen for a pair
 * that ``gs_ls_edges`` does not name. The confirmation says "unbekannt" there
 * rather than guessing an axis — telling an operator they are overruling a
 * ``native`` join when they are not is worse than admitting we do not know.
 */
export function resolveDocumentLinkKind(
  edges: readonly { settlementEventId: string; deliveryEventId: string; linkKind: string }[],
  settlementEventId: string,
  deliveryEventId: string,
): string | null {
  const hit = edges.find(
    (e) => e.settlementEventId === settlementEventId && e.deliveryEventId === deliveryEventId,
  );
  return hit ? hit.linkKind : null;
}
